import * as React from 'react';
import { Datagrid } from 'react-admin';
import { Box } from '@mui/material';
import { SCROLLABLE_DATAGRID_SX } from '../misc/tabbedListLayout';

const MIN_COLUMN_WIDTH = 72;

const readStoredWidths = (storageKey) => {
  if (!storageKey) return {};
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const columnKeyForTh = (th, index) => {
  const source = th.getAttribute('data-field') || th.getAttribute('data-source');
  if (source) return source;
  const colClass = Array.from(th.classList || []).find((c) => c.startsWith('column-'));
  if (colClass && colClass !== 'column-undefined') {
    return colClass.replace(/^column-/, '');
  }
  const label = (th.textContent || '').trim().toLowerCase().replace(/\s+/g, '-');
  return label || `col-${index}`;
};

/**
 * react-admin Datagrid + satış DataGrid’teki gibi sürüklenebilir kolon ayıracı.
 * MIT DataGrid değil; mevcut List/Field yapısı korunur.
 */
const ResizableDatagrid = ({ storageKey, sx, children, ...rest }) => {
  const rootRef = React.useRef(null);
  const widthsRef = React.useRef(readStoredWidths(storageKey));

  const persistWidth = React.useCallback(
    (key, width) => {
      if (!key || width == null) return;
      const nextWidth = Math.max(MIN_COLUMN_WIDTH, width);
      const next = { ...widthsRef.current, [key]: nextWidth };
      widthsRef.current = next;
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          /* ignore quota */
        }
      }
    },
    [storageKey],
  );

  const applyStoredWidths = React.useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const table = root.querySelector('table');
    if (!table) return;

    table.style.tableLayout = 'fixed';
    const headers = root.querySelectorAll('thead th');
    headers.forEach((th, index) => {
      const key = columnKeyForTh(th, index);
      const stored = widthsRef.current[key];
      if (stored != null) {
        th.style.width = `${stored}px`;
        th.style.minWidth = `${stored}px`;
        th.style.maxWidth = `${stored}px`;
      } else if (!th.style.width) {
        const w = Math.max(MIN_COLUMN_WIDTH, Math.round(th.getBoundingClientRect().width) || 150);
        th.style.width = `${w}px`;
        th.style.minWidth = `${MIN_COLUMN_WIDTH}px`;
      }
    });
  }, []);

  React.useEffect(() => {
    widthsRef.current = readStoredWidths(storageKey);
    applyStoredWidths();
  }, [storageKey, applyStoredWidths]);

  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    let detachMoveUp = null;
    const observer = new MutationObserver(() => {
      applyStoredWidths();
    });
    observer.observe(root, { childList: true, subtree: true });

    // İlk boyama sonrası uygula
    const raf = requestAnimationFrame(applyStoredWidths);

    const onSeparatorDown = (event) => {
      if (event.button !== 0) return;
      const th = event.target.closest?.('th');
      if (!th || !root.contains(th)) return;

      const rect = th.getBoundingClientRect();
      if (rect.right - event.clientX > 14) return;

      event.preventDefault();
      event.stopPropagation();

      const headers = Array.from(root.querySelectorAll('thead th'));
      const index = headers.indexOf(th);
      const key = columnKeyForTh(th, index);
      const startWidth = Math.max(MIN_COLUMN_WIDTH, Math.round(rect.width));
      const startX = event.clientX;
      let rafId = 0;
      let pendingWidth = startWidth;

      const applyWidth = (width) => {
        pendingWidth = width;
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          rafId = 0;
          th.style.width = `${pendingWidth}px`;
          th.style.minWidth = `${pendingWidth}px`;
          th.style.maxWidth = `${pendingWidth}px`;
        });
      };

      const onMove = (moveEvent) => {
        applyWidth(Math.max(MIN_COLUMN_WIDTH, startWidth + (moveEvent.clientX - startX)));
      };

      const onUp = (upEvent) => {
        document.removeEventListener('mousemove', onMove, true);
        document.removeEventListener('mouseup', onUp, true);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        detachMoveUp = null;
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = 0;
        }
        const finalWidth = Math.max(MIN_COLUMN_WIDTH, startWidth + (upEvent.clientX - startX));
        th.style.width = `${finalWidth}px`;
        th.style.minWidth = `${finalWidth}px`;
        th.style.maxWidth = `${finalWidth}px`;
        persistWidth(key, finalWidth);
      };

      detachMoveUp = () => {
        document.removeEventListener('mousemove', onMove, true);
        document.removeEventListener('mouseup', onUp, true);
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove, true);
      document.addEventListener('mouseup', onUp, true);
    };

    root.addEventListener('mousedown', onSeparatorDown, true);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      if (detachMoveUp) detachMoveUp();
      root.removeEventListener('mousedown', onSeparatorDown, true);
    };
  }, [applyStoredWidths, persistWidth]);

  return (
    <Box
      ref={rootRef}
      sx={{
        width: '100%',
        ...SCROLLABLE_DATAGRID_SX,
        '& table': {
          width: 'max-content',
          minWidth: '100%',
          tableLayout: 'fixed',
        },
        '& thead th': {
          position: 'relative',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          paddingRight: '16px !important',
          // Sağ kenar ayraç hit-area
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: 14,
            cursor: 'col-resize',
            zIndex: 2,
          },
        },
        '& thead th::after': {
          content: '""',
          position: 'absolute',
          top: '50%',
          right: 4,
          transform: 'translateY(-50%)',
          width: 2,
          height: 16,
          borderRadius: '2px',
          backgroundColor: 'rgba(0, 0, 0, 0.45)',
          pointerEvents: 'none',
          zIndex: 3,
        },
        '& thead th:hover::after': {
          backgroundColor: '#1976d2',
          height: 20,
        },
        '& tbody td': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
        },
        ...sx,
      }}
    >
      <Datagrid {...rest}>{children}</Datagrid>
    </Box>
  );
};

export default ResizableDatagrid;
